/**
 * 管理后台 · 系统设置（见方案 2B.3 A 类：注册开关 + 邀请码生成）。
 *
 * **只有四个字段**：家庭名、时区、注册开关、邀请码开关。
 * 密钥（JWT_SECRET / RESEND_API_KEY / 数据库密码）刻意不在这里出现 ——
 * 见方案 2B.3 D 类："UI 能写密钥之后，一次 XSS 或误操作就能让全家登不上"。
 *
 * 邀请码生成后只显示一次：它等价于一次注册凭据。
 */
import { useEffect, useState } from 'react';
import { Badge, Card, CardHeader, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { AdminShell } from '../../components/layout/AdminShell';
import { ManualCommands } from '../../components/admin/AdminBits';
import { useAdminInviteCode, useAdminSettings, useAdminSettingsMutation } from '../../lib/queries';
import { toErrorMessage } from '../../lib/api/error';
import { formatDateTimeFull } from '../../lib/utils';

/** 常用时区（家庭自用，给几个常见选项 + 保留手填）。 */
const TIMEZONE_PRESETS = [
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Taipei',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Europe/London',
  'America/Los_Angeles',
  'America/New_York',
  'UTC',
];

export function AdminSettingsPage() {
  const toast = useToast();
  const settingsQuery = useAdminSettings();
  const mutation = useAdminSettingsMutation();
  const invite = useAdminInviteCode();

  const [familyName, setFamilyName] = useState('');
  const [timezone, setTimezone] = useState('Asia/Shanghai');
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const [requireInviteCode, setRequireInviteCode] = useState(false);
  const [ttlMinutes, setTtlMinutes] = useState(1440);
  const [issuedCode, setIssuedCode] = useState<{ code: string; expiresAt: string } | null>(null);

  // 拉回来之后填进表单（失败时保持默认值，不阻塞页面）
  useEffect(() => {
    const data = settingsQuery.data;
    if (!data) return;
    setFamilyName(data.familyName);
    setTimezone(data.timezone);
    setRegistrationOpen(data.registrationOpen);
    setRequireInviteCode(data.requireInviteCode);
  }, [settingsQuery.data]);

  const onSave = async () => {
    try {
      await mutation.mutateAsync({ familyName, timezone, registrationOpen, requireInviteCode });
      toast.showToast('设置已保存（已写入审计日志）', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  const onCreateInvite = async () => {
    try {
      const result = await invite.mutateAsync(ttlMinutes);
      setIssuedCode({ code: result.code, expiresAt: result.expiresAt });
      toast.showToast('邀请码已生成，请立即复制', 'success');
    } catch (err) {
      toast.showToast(toErrorMessage(err), 'error');
    }
  };

  return (
    <AdminShell
      title="系统设置"
      description="只影响这个部署实例的开关与家庭基本信息；不涉及任何密钥。"
    >
      {settingsQuery.isLoading && <Skeleton className="h-72" />}

      {settingsQuery.isError && (
        <Card>
          <p role="alert" className="text-sm text-rose-700">
            读取设置失败：{toErrorMessage(settingsQuery.error)}
          </p>
        </Card>
      )}

      {settingsQuery.data && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="基本信息" description="家庭名与家庭时区（决定「今天」的判定）" />
            <div className="space-y-4">
              <Input
                label="家庭名称"
                value={familyName}
                maxLength={32}
                onChange={(e) => setFamilyName(e.target.value)}
              />
              <Select label="时区" value={TIMEZONE_PRESETS.includes(timezone) ? timezone : 'custom'} onChange={(e) => {
                if (e.target.value !== 'custom') setTimezone(e.target.value);
              }}>
                {TIMEZONE_PRESETS.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
                {!TIMEZONE_PRESETS.includes(timezone) && <option value="custom">{timezone}</option>}
              </Select>
              <Input
                label="自定义时区（IANA 名称）"
                hint={'例如 Asia/Shanghai。填错会让"今天"整体偏移，后端会用 Intl 校验后拒绝。'}
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              />
              <p className="text-xs text-slate-500">
                当前生效时区：<strong>{settingsQuery.data.timezone}</strong>
              </p>
            </div>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader
                title="注册开关"
                description="站点在公网，默认建议关闭开放注册，改用邀请码把家人拉进来"
              />
              <div className="space-y-3">
                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={registrationOpen}
                    onChange={(e) => setRegistrationOpen(e.target.checked)}
                  />
                  <span className="text-sm text-slate-700">
                    允许任何人注册家长账号
                    <span className="ml-2">
                      {registrationOpen ? (
                        <Badge tone="warn">开放中，公网可注册</Badge>
                      ) : (
                        <Badge tone="success">已关闭</Badge>
                      )}
                    </span>
                  </span>
                </label>

                <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={requireInviteCode}
                    onChange={(e) => setRequireInviteCode(e.target.checked)}
                  />
                  <span className="text-sm text-slate-700">
                    注册必须填写邀请码
                    <span className="ml-2">
                      {requireInviteCode ? (
                        <Badge tone="brand">需要邀请码</Badge>
                      ) : (
                        <Badge tone="neutral">不需要</Badge>
                      )}
                    </span>
                  </span>
                </label>
              </div>

              <div className="mt-4 flex gap-2">
                <Button loading={mutation.isPending} onClick={() => void onSave()}>
                  保存设置
                </Button>
                <Button
                  variant="secondary"
                  disabled={mutation.isPending}
                  onClick={() => {
                    const data = settingsQuery.data;
                    if (!data) return;
                    setFamilyName(data.familyName);
                    setTimezone(data.timezone);
                    setRegistrationOpen(data.registrationOpen);
                    setRequireInviteCode(data.requireInviteCode);
                  }}
                >
                  撤销改动
                </Button>
              </div>
            </Card>

            <Card>
              <CardHeader
                title="生成家长邀请码"
                description="与命令行 operator.ts invite-code 是同一段逻辑；明文只显示一次"
              />
              <div className="flex flex-wrap items-end gap-3">
                <Input
                  label="有效期（分钟）"
                  type="number"
                  min={5}
                  max={43200}
                  value={String(ttlMinutes)}
                  onChange={(e) => setTtlMinutes(Number(e.target.value) || 1440)}
                  className="w-40"
                />
                <Button loading={invite.isPending} onClick={() => void onCreateInvite()}>
                  生成邀请码
                </Button>
              </div>

              {issuedCode && (
                <div className="mt-3 rounded-xl bg-emerald-50 p-3">
                  <p className="text-xs text-emerald-800">
                    {/*
                      到期时间原来直接切 ISO 字符串（`slice(0, 16)`），那是 **UTC 时间**且
                      没有时区提示 —— 一个 "10-20 08:00" 看着像本地时间，实际差 8 小时。
                      这里改用全站统一的家庭时区格式（`YYYY-MM-DD HH:mm`），与账号页同一套。
                    */}
                    邀请码（只显示这一次，到期时间 {formatDateTimeFull(issuedCode.expiresAt)}）
                  </p>
                  <code className="mt-1 block text-center text-2xl font-bold tracking-[0.3em] text-emerald-900">
                    {issuedCode.code}
                  </code>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="mt-2"
                    onClick={() => {
                      void navigator.clipboard?.writeText(issuedCode.code).then(
                        () => toast.showToast('已复制', 'success'),
                        () => toast.showToast('复制失败，请手动选中复制', 'error'),
                      );
                    }}
                  >
                    复制
                  </Button>
                </div>
              )}
            </Card>

            <Card>
              <CardHeader title="密钥不在这里" description="刻意的设计，不是遗漏" />
              <p className="text-sm text-slate-600">
                JWT 密钥、邮件服务 API Key、数据库口令都<strong>不能</strong>通过网页修改：
                一次误操作或 XSS 就能让全家登不上，而密钥轮换必须是有人负责的手工操作。
              </p>
              <ManualCommands
                commands={[
                  '# 修改密钥的正确姿势：在服务器上编辑 deploy/.env，然后只重建需要的服务',
                  'sudo vi /opt/family-habit-tracker/deploy/.env',
                  'docker compose -f deploy/docker-compose.yml up -d api worker',
                ]}
              />
            </Card>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
